import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  // Validated internal return path only (no open redirect): must be a single-slash absolute path.
  const rawNext = searchParams.get("next");
  const safeNext = rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//") && !rawNext.includes("://") ? rawNext : null;
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [ackPrivacy, setAckPrivacy] = useState(false);
  const [versions, setVersions] = useState({});

  useEffect(() => {
    api.get("/legal/versions").then(({ data }) => setVersions(data)).catch(() => {});
  }, []);

  const googleLogin = () => {
    // Preserve the intended internal destination across the OAuth round-trip (read back in AuthCallback).
    // This does NOT alter the redirect URL below; the session exchange is unchanged.
    if (safeNext) sessionStorage.setItem("ts_post_auth_next", safeNext);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome to TenantSeal");
        navigate(safeNext || "/dashboard");
      } else {
        if (!name.trim()) { toast.error("Please enter your name"); setBusy(false); return; }
        if (!acceptTerms || !ackPrivacy) { toast.error("Please agree to the Terms and acknowledge the Privacy Policy."); setBusy(false); return; }
        await register(name, email, password, {
          accept_terms: acceptTerms,
          acknowledge_privacy: ackPrivacy,
          terms_version: versions.terms_version,
          privacy_version: versions.privacy_version,
        });
        toast.success("Welcome to TenantSeal");
        navigate(safeNext || "/welcome");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#EEF2F6]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-navy text-white p-12 relative overflow-hidden dd-grain">
        <button onClick={() => navigate("/")} data-testid="login-back-btn" className="relative flex items-center gap-2 text-white/70 hover:text-white transition-colors w-fit">
          <ArrowLeft size={18} /> Back home
        </button>
        <div className="relative">
          <h2 className="font-heading text-4xl font-bold leading-tight">Your deposit,<br />defended by evidence.</h2>
          <p className="mt-4 text-white/70 text-lg max-w-md leading-relaxed">
            Join renters using AI-powered documentation to organize evidence and prepare for a deposit dispute.
          </p>
          <div className="mt-8 flex items-center gap-3 text-white/80">
            <ShieldCheck className="text-amber" /> <span>Timestamped, tamper-evident photo records</span>
          </div>
        </div>
        <Logo dark />
      </div>

      {/* Right form */}
      <div className="flex flex-col items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-navy">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-2 text-[#475569]">
            {mode === "login" ? "Log in to manage your properties." : "Start protecting your deposit in minutes."}
          </p>

          <Button
            data-testid="google-login-btn"
            onClick={googleLogin}
            variant="outline"
            className="mt-6 w-full h-12 rounded-xl border-[#E2E8F0] hover:bg-[#FAF7F2] font-medium"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="mr-2 h-5 w-5" />
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs text-[#94A3B8]">
            <div className="h-px flex-1 bg-[#E2E8F0]" /> OR <div className="h-px flex-1 bg-[#E2E8F0]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-[#0F172A]">Full name</Label>
                <Input id="name" data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Renter" className="mt-1.5 h-12 rounded-xl" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-[#0F172A]">Email</Label>
              <Input id="email" data-testid="email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 h-12 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-[#0F172A]">Password</Label>
              <Input id="password" data-testid="password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 h-12 rounded-xl" />
            </div>
            {mode === "register" && (
              <div className="space-y-2.5 pt-1" data-testid="signup-consent">
                <label className="flex items-start gap-2 text-sm text-[#475569] cursor-pointer">
                  <Checkbox data-testid="accept-terms" checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} className="mt-0.5" />
                  <span>I agree to the <Link to="/terms" target="_blank" className="font-medium text-brand hover:underline">Terms of Service</Link>.</span>
                </label>
                <label className="flex items-start gap-2 text-sm text-[#475569] cursor-pointer">
                  <Checkbox data-testid="acknowledge-privacy" checked={ackPrivacy} onCheckedChange={(v) => setAckPrivacy(!!v)} className="mt-0.5" />
                  <span>I acknowledge the <Link to="/privacy" target="_blank" className="font-medium text-brand hover:underline">Privacy Policy</Link>.</span>
                </label>
                <p className="text-xs text-[#94A3B8]">TenantSeal is intended for users age 18 or older.</p>
              </div>
            )}
            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={busy || (mode === "register" && (!acceptTerms || !ackPrivacy))}
              className="w-full h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-medium shadow-[0_4px_14px_rgba(15,27,45,0.28)]"
            >
              {busy ? <Loader2 className="animate-spin" size={18} /> : mode === "login" ? "Log in" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#475569]">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-semibold text-brand hover:underline"
            >
              {mode === "login" ? "Create an account" : "Log in"}
            </button>
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs" data-testid="login-legal-links">
            <Link to="/terms" className="text-[#94A3B8] hover:text-brand">Terms</Link>
            <Link to="/privacy" className="text-[#94A3B8] hover:text-brand">Privacy</Link>
            <Link to="/ai-limitations" className="text-[#94A3B8] hover:text-brand">AI & Evidence Limitations</Link>
            <Link to="/data-deletion" className="text-[#94A3B8] hover:text-brand">Data Deletion</Link>
            <Link to="/acceptable-use" className="text-[#94A3B8] hover:text-brand">Acceptable Use</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
